// VOY-417 (rewrite, post-audit) — /you page.
//
// Single-column "travel second brain": inferred readout + correction
// surface. Eight sections, each fed by a parallel slice of the user's
// state. Three of the sections (§3 per-attribute Correct, §7 memory-feed
// Forget, §8 Memory Editor) wire to ONE shared correction flow via
// useFactCorrection — same VOY-458 backend, three entry points.
//
// Plumbing preserved from the legacy /you:
//   - useAuth() auth gate
//   - No layout shift on load (skeletons at loaded dimensions)
//
// Everything else is rewritten. The old insights-driven /you with
// localStorage cache + InsightCard is fully superseded by this page —
// the InsightCard component stays unmounted but un-deleted in case
// another consumer renders it (none found in the audit).
//
// Stale-spec deviations flagged in the PR:
//   1. Per-attribute Correct: only the 3 style_attribute keys with a
//      PROFILE_DIMENSIONS counterpart (hotel_tier, trip_length,
//      booking_window) actually round-trip through VOY-458. The other 5
//      render the Correct button disabled with a "Backend coverage
//      pending" tooltip. Backend follow-up: extend PROFILE_DIMENSIONS.
//   2. Memory-feed Forget: VOY-458's /delete is dimension-keyed; memory
//      feed entries are untyped observations. Forget renders disabled
//      with a wire-point until the backend gains an observation-index
//      endpoint OR semantic dimension derivation. Memory Editor (§8)
//      remains the working delete surface for typed facts.
//   3. Saved-by-Voy stat in §5 stubbed (VOY-420 not shipped).

import { useEffect, useState, type ReactNode } from 'react'
import { useAuth } from '@/lib/auth'
import {
  getUserProfile,
  getProfileCompleteness,
  getAgentProfile,
  getAgentContext,
  getSignalStats,
  type SignalStats,
} from '@/lib/profile'
import type { Profile, ProfileCompleteness, AgentProfile, AgentContext } from '@/types/profile'

import { TravelPersonalitySection } from '@/components/you/v417/TravelPersonalitySection'
import { ProfileStrengthSection } from '@/components/you/v417/ProfileStrengthSection'
import { TravelStyleSection } from '@/components/you/v417/TravelStyleSection'
import { KeywordsSection } from '@/components/you/v417/KeywordsSection'
import { SignalsSection } from '@/components/you/v417/SignalsSection'
import { RecentSearchesContextSection } from '@/components/you/v417/RecentSearchesContextSection'
import { MemoryFeedSection } from '@/components/you/v417/MemoryFeedSection'
import { MemoryEditorSection } from '@/components/you/v417/MemoryEditorSection'
import { useFactCorrection } from '@/components/you/v417/useFactCorrection'

export default function YouPage(): ReactNode {
  const { user } = useAuth()
  const [profile, setProfile] = useState<Profile | null>(null)
  const [completeness, setCompleteness] = useState<ProfileCompleteness | null>(null)
  const [agentProfile, setAgentProfile] = useState<AgentProfile | null>(null)
  const [agentContext, setAgentContext] = useState<AgentContext | null>(null)
  const [signals, setSignals] = useState<SignalStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  // Shared correction flow for §3, §7, §8. onSuccess refetches the agent
  // context + agent profile so a Correct flips the rendered value
  // immediately (the override outranks the inference in the resolver).
  //
  // Single-flight by design: while one section has a request in flight,
  // `correction.state.saving` is true for every consumer of this hook —
  // so an in-flight §3 Correct disables §8's row actions too. That's
  // deliberate; concurrent writes to the same dimension would race.
  // The error state is shared the same way — each section's
  // `resetError()` clears it on modal close (or row action complete).
  const correction = useFactCorrection({
    userId: user?.id ?? '',
    onSuccess: () => {
      // Refetch the slices that depend on overrides. The signal stats,
      // recent searches, and keywords aren't affected by Memory Editor
      // writes, so skip those.
      void refreshAgentSlices()
    },
  })

  useEffect(() => {
    if (!user?.id) {
      setLoading(false)
      return
    }
    void loadAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  async function loadAll() {
    if (!user?.id) return
    setLoading(true)
    setLoadError(null)
    try {
      // VOY-417 review #4: fetch the profile first, then thread it into
      // getProfileCompleteness so the latter doesn't redo the same
      // Supabase auth + profiles round-trip. The agent slices + signal
      // stats run in parallel because they're independent of profile.
      const profileData = await getUserProfile()
      if (!profileData) {
        setLoadError("We couldn't load your profile. Sign in again or try once more.")
        return
      }
      const [
        completenessData,
        agentProfileData,
        agentContextData,
        signalStatsData,
      ] = await Promise.all([
        getProfileCompleteness(user.id, profileData).catch((err) => {
          console.error('[YouPage] completeness load failed:', err)
          return null
        }),
        getAgentProfile(user.id).catch((err) => {
          console.error('[YouPage] agent profile load failed:', err)
          return null
        }),
        getAgentContext(user.id).catch((err) => {
          console.error('[YouPage] agent context load failed:', err)
          return null
        }),
        getSignalStats(user.id).catch((err) => {
          console.error('[YouPage] signals load failed:', err)
          return null
        }),
      ])
      setProfile(profileData)
      setCompleteness(completenessData)
      setAgentProfile(agentProfileData)
      setAgentContext(agentContextData)
      setSignals(signalStatsData)
    } catch (err) {
      console.error('[YouPage] loadAll failed:', err)
      setLoadError(err instanceof Error ? err.message : 'Failed to load your insights.')
    } finally {
      setLoading(false)
    }
  }

  async function refreshAgentSlices() {
    if (!user?.id) return
    try {
      const [agentProfileData, agentContextData] = await Promise.all([
        getAgentProfile(user.id).catch(() => null),
        getAgentContext(user.id).catch(() => null),
      ])
      setAgentProfile(agentProfileData)
      setAgentContext(agentContextData)
    } catch (err) {
      console.error('[YouPage] refreshAgentSlices failed:', err)
    }
  }

  const firstName = profile?.name?.trim() || profile?.username?.trim() || 'traveler'

  if (loading && !profile) {
    return (
      <div className="voy-v6">
        <div className="page you-page">
          <div className="profile-loading">Loading your travel profile…</div>
        </div>
      </div>
    )
  }

  if (loadError || !profile) {
    return (
      <div className="voy-v6">
        <div className="page you-page">
          <div className="profile-loading" style={{ flexDirection: 'column', gap: 12 }}>
            <p style={{ margin: 0 }}>{loadError ?? "We couldn't load your travel profile."}</p>
            <button
              type="button"
              className="btn-primary"
              style={{ padding: '7px 14px', fontSize: 12 }}
              onClick={() => void loadAll()}
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="voy-v6">
      <div className="page you-page">
        <header className="ph">
          <div className="eyebrow"><span className="dot" />SECOND BRAIN</div>
          <h1>
            Hi <span className="serif-i">{firstName}</span> — here&apos;s your travel profile.
          </h1>
          <p className="lede">
            What Voy has learned about you, where it&apos;s strong, where it&apos;s still guessing.
            Correct anything wrong — your corrections always win.
          </p>
        </header>

        <div className="you-stack">
          <TravelPersonalitySection agentProfile={agentProfile} loading={loading} />
          <ProfileStrengthSection completeness={completeness} loading={loading} />
          <TravelStyleSection
            agentProfile={agentProfile}
            loading={loading}
            correction={correction}
          />
          <KeywordsSection userId={profile.id} />
          <SignalsSection stats={signals} loading={loading} />
          <RecentSearchesContextSection userId={profile.id} />
          <MemoryFeedSection agentProfile={agentProfile} loading={loading} />
          <MemoryEditorSection
            resolvedPreferences={agentContext?.resolved_preferences ?? null}
            loading={loading}
            correction={correction}
          />
        </div>

        <footer className="app-foot">
          <div className="row"><span>© 2026 Voy</span></div>
          <div className="row" style={{ fontFamily: 'var(--v6-font-mono)', fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            <span style={{ color: 'var(--v6-pos)' }}>●</span> LIVE · ALL SYSTEMS GO
          </div>
        </footer>
      </div>
    </div>
  )
}
